import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';

// -----------------------------------------------------------------------------
interface AdminStats {
  totalPatients: number;
  totalPractitioners: number;
  totalOrders: number;
  totalTasks: number;
}

// -----------------------------------------------------------------------------
interface UseAdminStatsReturn {
  stats: AdminStats;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// -----------------------------------------------------------------------------
const baseStatsSearchParams = { _total: 'estimate', _count: '0' };

// -----------------------------------------------------------------------------
export const useAdminStats = (): UseAdminStatsReturn => {
  const medplum = useMedplum();
  const { t } = useTranslation('admin');

  const [stats, setStats] = useState<AdminStats>({
    totalPatients: 0,
    totalPractitioners: 0,
    totalOrders: 0,
    totalTasks: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load basic statistics
      const [patients, practitioners, orders, tasks] = await Promise.all([
        medplum.searchResources('Patient', baseStatsSearchParams),
        medplum.searchResources('Practitioner', baseStatsSearchParams),
        medplum.searchResources('ServiceRequest', baseStatsSearchParams),
        medplum.searchResources('Task', baseStatsSearchParams),
      ]);

      setStats({
        totalPatients: patients?.bundle?.total ?? 0,
        totalPractitioners: practitioners?.bundle?.total ?? 0,
        totalOrders: orders?.bundle?.total ?? 0,
        totalTasks: tasks?.bundle?.total ?? 0,
      });
    } catch (err) {
      console.error(t('errors.loadingStats'), err);
      setError(t('errors.loadingStats'));
    } finally {
      setLoading(false);
    }
  }, [medplum, t]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // ---------------------------------------------------------------------------
  return {
    stats,
    loading,
    error,
    refetch: loadStats,
  };
};
